import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./authContext";
import {
    aplicativoDaRota,
    aplicativosPermitidosDoUsuario,
    configDoAplicativo,
    salvarAplicativoAtual,
} from "../config/applications";

const AppContext = createContext();

/**
 * Contexto central de "qual aplicativo está ativo" (MyDash / MyDash Solicitações). Fica dentro do
 * AuthProvider (precisa de `user`) e dentro do BrowserRouter (precisa de useLocation/useNavigate).
 * Único lugar que decide isso — Navbar, RoleBasedRoute etc. só consultam `useApp()`, sem precisar
 * de `if (isSolicitacoes)` espalhado.
 */
export function AppProvider({ children }) {
    const { user, authenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const aplicativosPermitidos = useMemo(
        () => (authenticated ? aplicativosPermitidosDoUsuario(user) : []),
        [user, authenticated]
    );

    const [aplicativoAtual, setAplicativoAtual] = useState(() => aplicativoDaRota(location.pathname).key);

    // Mantém o contexto em sincronia com a URL — cobre refresh, link direto e navegação livre,
    // sem isso o Navbar podia continuar mostrando o menu do app anterior numa rota que já pertence
    // ao outro aplicativo.
    useEffect(() => {
        const daRota = aplicativoDaRota(location.pathname).key;
        if (daRota !== aplicativoAtual) {
            setAplicativoAtual(daRota);
            salvarAplicativoAtual(daRota);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    function trocarAplicativo(key) {
        const destino = configDoAplicativo(key);
        salvarAplicativoAtual(key);
        setAplicativoAtual(key);
        navigate(destino.rotaInicial);
    }

    const configAtual = configDoAplicativo(aplicativoAtual);

    return (
        <AppContext.Provider value={{ aplicativoAtual, configAtual, aplicativosPermitidos, trocarAplicativo }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useApp precisa ser usado dentro de um AppProvider");
    }
    return context;
}
